import React, { useState, useCallback, useEffect, useRef } from 'react'
import { Status, Footer, Button, Message } from '@iqueue/ui-kit'
import HelpersPresenter from '../api/helpersPresenter'
import { t } from '../hooks/useTranslate'
import OptionPresenter from '../api/optionPresenter'
import usePermissions from './usePermissions'

function StatusCard({ title, statuses, onDrop, onDragOver, onDragStart, statusType, isDragOver, isDraggable }) {
  const lastStatus = useRef('')
  const [weakStatus, setWeakStatus] = useState(true)

  const handleDragOverCard = useCallback((e) => {
    if (!isDraggable) return

    e.preventDefault()
    setWeakStatus(false)
    onDragOver(e)
  }, [onDragOver, isDragOver])

  const handleDragLeaveCard = useCallback(() => {
    setWeakStatus(true)
  }, [])

  const handleDropCard = useCallback((e) => {
    if (!isDraggable) return
    onDrop(e)
    setWeakStatus(true)
  }, [onDrop, isDraggable])

  const handleGlobalDragEnd = useCallback(() => {
    setWeakStatus(true)
  }, [])

  useEffect(() => {
    window.addEventListener('dragend', handleGlobalDragEnd)
    return () => {
      window.removeEventListener('dragend', handleGlobalDragEnd)
    }
  }, [handleGlobalDragEnd])

  return <Status
    inline
    weak={ weakStatus }
    warning={ statusType === 'warning' }
    error={ statusType === 'error' }
    className={ `status-card ${ isDragOver ? `highlight-${ statusType }` : '' } ${ !isDraggable ? 'disabled' : '' }` }
  >
    <b>{ title }</b>
    <div
      className="status-card-content"
      onDrop={ handleDropCard }
      onDragOver={ handleDragOverCard }
      onDragLeave={ handleDragLeaveCard }
    >
      { statuses.length > 0 ? (
        statuses.map((status, index) => (
          <div
            key={ index }
            draggable={ isDraggable }
            onDragStart={ (e) => isDraggable && onDragStart(e, status) }
            className={ `card-status-chip ${ statusType }` }
          >

            <b>{ t(`status.node.${ status }`) }</b>
          </div>
        ))
      ) : (
        <b className="placeholder-text">{ t('optionPage.droppableStatusCard.placeholder') }</b>
      ) }
    </div>
  </Status>
}

export default function DroppableStatusCard() {
  const [workingStatuses, setWorkingStatuses] = useState([])
  const [warningStatuses, setWarningStatuses] = useState([])
  const [errorStatuses, setErrorStatuses] = useState([])
  const [initialStatuses, setInitialStatuses] = useState(null)
  const [dragOverTarget, setDragOverTarget] = useState('')
  const [recordId, setRecordId] = useState(null)
  const permission = usePermissions()
  const hasUpdatePermission = permission.permissions?.OPTION?.includes('UPDATE')

  const handleDragStart = useCallback((e, status) => {
    const el = document.getElementById('status-card-wrapper')
    el.dataset.status = status
    e.dataTransfer.setData('status', status)
    e.dataTransfer.effectAllowed = 'move'
  }, [])

  const handleDrop = useCallback((e, targetSetter) => {
    e.preventDefault()

    const el = document.getElementById('status-card-wrapper')
    const status = e.dataTransfer.getData('status') || el.dataset.status
    if (!status) return

    setWorkingStatuses((prev) => prev.filter((s) => s !== status))
    setWarningStatuses((prev) => prev.filter((s) => s !== status))
    setErrorStatuses((prev) => prev.filter((s) => s !== status))

    targetSetter((prev) => [...prev, status])

    setDragOverTarget('')
  }, [])

  const handleDragOver = useCallback((e, target) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDragOverTarget(target)
  }, [])

  const handleDragLeave = useCallback(() => {
    setDragOverTarget('')
  }, [])

  const handleSave = useCallback(() => {
    const updatedOption = {
      value: {
        working: workingStatuses,
        warning: warningStatuses,
        error: errorStatuses,
      },
    }

    OptionPresenter.post('', {
      id: recordId || undefined,
      key: 'nodeStatuses',
      ...updatedOption,
    }).then(() => {
      Message({
        type: 'success',
        timeout: 3000,
        title: 'Статусы успешно обновлены!',
      })
      setInitialStatuses(updatedOption.value)
    }).catch(err => console.error(err))

  }, [workingStatuses, warningStatuses, errorStatuses, recordId])

  const fetchOrCreateOption = useCallback(() => {
    OptionPresenter.get('get-by-key/nodeStatuses').then((res) => {
      if (res) {
        setRecordId(res.id)

        const groupedStatuses = res.value || { working: [], warning: [], error: [] }

        setWorkingStatuses(groupedStatuses.working || [])
        setWarningStatuses(groupedStatuses.warning || [])
        setErrorStatuses(groupedStatuses.error || [])

        setInitialStatuses(groupedStatuses)
      }
    }).catch(() => {
      HelpersPresenter.get('nodes/statuses').then((statuses) => {
        setWorkingStatuses(Object.values(statuses))
        setInitialStatuses({ working: [], warning: [], error: [] })
      })
    })
  }, [])

  const hasChanges = useCallback(() => {
    if (!initialStatuses) return false
    return JSON.stringify(initialStatuses.working) !== JSON.stringify(workingStatuses) ||
      JSON.stringify(initialStatuses.warning) !== JSON.stringify(warningStatuses) ||
      JSON.stringify(initialStatuses.error) !== JSON.stringify(errorStatuses)
  }, [initialStatuses, workingStatuses, warningStatuses, errorStatuses])

  useEffect(() => {
    fetchOrCreateOption()
  }, [])

  return <div className="option-page">
    <div id="status-card-wrapper" className="status-card-container">
      <StatusCard
        title={ t('optionPage.droppableStatusCard.statuses.working') }
        statuses={ workingStatuses }
        onDragStart={ handleDragStart }
        onDrop={ (e) => handleDrop(e, setWorkingStatuses) }
        onDragOver={ (e) => handleDragOver(e, 'working') }
        onDragLeave={ handleDragLeave }
        isDraggable={ hasUpdatePermission }
        statusType="working"
        isDragOver={ dragOverTarget === 'working' }
      />

      <StatusCard
        title={ t('optionPage.droppableStatusCard.statuses.warning') }
        statuses={ warningStatuses }
        onDragStart={ handleDragStart }
        onDrop={ (e) => handleDrop(e, setWarningStatuses) }
        onDragOver={ (e) => handleDragOver(e, 'warning') }
        onDragLeave={ handleDragLeave }
        isDraggable={ hasUpdatePermission }
        statusType="warning"
        isDragOver={ dragOverTarget === 'warning' }
      />

      <StatusCard
        title={ t('optionPage.droppableStatusCard.statuses.error') }
        statuses={ errorStatuses }
        onDragStart={ handleDragStart }
        onDrop={ (e) => handleDrop(e, setErrorStatuses) }
        onDragOver={ (e) => handleDragOver(e, 'error') }
        onDragLeave={ handleDragLeave }
        isDraggable={ hasUpdatePermission }
        statusType="error"
        isDragOver={ dragOverTarget === 'error' }
      />
    </div>
    { hasUpdatePermission && <Footer>
      <Button
        disabled={ !hasChanges() }
        title={ t('optionPage.droppableStatusCard.saveButton') }
        onClick={ handleSave }
        primary
      />
    </Footer> }
  </div>
}
